import { useMemo } from 'react';
import { ArrowRight, Building2, FilePlus, FolderArchive, Hourglass, RotateCcw, Sparkles, Target, Trophy } from 'lucide-react';
import type { SavedProposalDoc } from '../ProposalsModal';
import { SectionCard } from './SectionCard';
import { StatTile } from './StatTile';
import { ActionList } from './ActionList';
import { RecentProposals } from './RecentProposals';
import { StatusBreakdown } from './StatusBreakdown';
import { MonthlyActivity } from './MonthlyActivity';
import {
  ACTIVITY_MONTHS, MAX_ACTIONS, buildHeadline, buildWelcomeMetrics, formatCompactTL, formatFullTL,
} from './metrics';

function greetingFor(now: Date): string {
  const h = now.getHours();
  if (h < 11) return 'Günaydın';
  if (h < 18) return 'İyi çalışmalar';
  return 'İyi akşamlar';
}

export interface WelcomeDashboardProps {
  /** The signed-in designer's own proposals — the only data this screen reports on. */
  proposals: SavedProposalDoc[];
  loading: boolean;
  firstName: string;
  /** Frozen per visit so every relative day count on the page agrees. */
  now: Date;
  onOpenProposal: (p: SavedProposalDoc) => void;
  onNewProposal: (type: 'retail' | 'bulk') => void;
  onOpenArchive: () => void;
  /**
   * Set when the retail editor still holds unsaved work (tarayıcı hafızasındaki
   * taslak — window kapatılıp geri dönüldüğünde bile kaybolmaz). null when
   * there is nothing to resume, e.g. right after "Yeni Teklif".
   */
  draftSummary?: { clientLabel: string; modelLabel: string } | null;
  onResumeDraft?: () => void;
}

/**
 * The welcome screen's presentation: greeting, four KPI tiles, the work the
 * designer should pick up next (left) and the shape of their portfolio (right).
 * Kept free of Firestore and app context so it can be rendered from fixtures.
 */
export function WelcomeDashboard({
  proposals, loading, firstName, now, onOpenProposal, onNewProposal, onOpenArchive,
  draftSummary = null, onResumeDraft,
}: WelcomeDashboardProps) {
  const metrics = useMemo(() => buildWelcomeMetrics(proposals, now), [proposals, now]);

  const dateLabel = now.toLocaleDateString('tr-TR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  const portfolioTotal = metrics.statusBreakdown.reduce((sum, s) => sum + s.total, 0);
  const visibleActions = metrics.actions.slice(0, MAX_ACTIONS);
  const hiddenActionCount = metrics.actions.length - visibleActions.length;

  return (
    <div className="no-print space-y-8">

      {/* Greeting + primary actions */}
      <header className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5">
        <div className="min-w-0">
          <p className="text-[11px] font-medium uppercase tracking-wider text-stone-400">{dateLabel}</p>
          <h1 className="mt-1.5 text-3xl md:text-4xl font-semibold tracking-tight text-stone-900">
            {greetingFor(now)}, {firstName || 'Kullanıcı'}
          </h1>
          <p className="mt-2 text-sm text-stone-500 leading-relaxed max-w-2xl">
            {loading ? 'Teklif özetin hazırlanıyor...' : buildHeadline(metrics)}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2.5 shrink-0">
          <button
            type="button"
            onClick={() => onNewProposal('retail')}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#945f46] hover:bg-[#83523c] text-white rounded-lg text-xs font-semibold transition-colors shadow-xs"
          >
            <FilePlus className="w-4 h-4" />
            Yeni Teklif
          </button>
          <button
            type="button"
            onClick={() => onNewProposal('bulk')}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-white hover:bg-stone-50 text-stone-700 border border-stone-200/90 rounded-lg text-xs font-medium transition-colors shadow-xs"
          >
            <Building2 className="w-4 h-4 text-stone-500" />
            Toplu Proje
          </button>
          <button
            type="button"
            onClick={onOpenArchive}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-white hover:bg-stone-50 text-stone-700 border border-stone-200/90 rounded-lg text-xs font-medium transition-colors shadow-xs"
          >
            <FolderArchive className="w-4 h-4 text-stone-500" />
            Tekliflerim
          </button>
        </div>
      </header>

      {draftSummary && onResumeDraft && (
        <button
          type="button"
          onClick={onResumeDraft}
          className="w-full flex items-center gap-4 px-5 py-4 bg-brand-50 hover:bg-brand-100 border border-brand-200/70 rounded-2xl text-left transition-colors group"
        >
          <div className="w-10 h-10 rounded-xl bg-white text-brand-600 flex items-center justify-center shrink-0 shadow-2xs">
            <RotateCcw className="w-4.5 h-4.5" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-xs font-extrabold text-brand-800">Kaldığın yerden devam et</p>
            <p className="mt-0.5 text-xs text-brand-700/80 truncate">
              Kaydedilmemiş bir teklif üzerinde çalışıyordun{draftSummary.clientLabel ? ` — ${draftSummary.clientLabel}` : ''}
              {draftSummary.modelLabel ? ` (${draftSummary.modelLabel})` : ''}
            </p>
          </div>
          <span className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 bg-white group-hover:bg-brand-600 group-hover:text-white text-brand-700 border border-brand-200/70 group-hover:border-brand-600 rounded-lg text-[11px] font-extrabold transition-colors">
            Devam Et
            <ArrowRight className="w-3.5 h-3.5" />
          </span>
        </button>
      )}

      {loading ? (
        <div className="space-y-6" aria-hidden>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[0, 1, 2, 3].map(i => <div key={i} className="h-[122px] rounded-2xl bg-white border border-stone-200/70 animate-pulse" />)}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div className="lg:col-span-7 h-72 rounded-2xl bg-white border border-stone-200/70 animate-pulse" />
            <div className="lg:col-span-5 h-72 rounded-2xl bg-white border border-stone-200/70 animate-pulse" />
          </div>
        </div>
      ) : metrics.total === 0 ? (
        <div className="bg-white rounded-2xl border border-stone-200/70 px-6 py-14 flex flex-col items-center text-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-brand-50 text-brand-500 flex items-center justify-center">
            <FilePlus className="w-6 h-6" />
          </div>
          <h2 className="text-sm font-extrabold text-stone-900">Henüz kayıtlı teklifin yok</h2>
          <p className="text-xs text-stone-500 max-w-sm leading-relaxed">
            İlk teklifini oluşturup buluta kaydettiğinde; aylık hacmin, takip etmen gereken teklifler ve
            kazanma oranın bu sayfada özetlenecek.
          </p>
        </div>
      ) : (
        <>
          {/* KPI row */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatTile
              emphasis
              label="Bu Ay Hazırlanan"
              icon={<Sparkles className="w-4 h-4" />}
              value={formatCompactTL(metrics.thisMonth.total)}
              valueTitle={formatFullTL(metrics.thisMonth.total)}
              hint={`${metrics.thisMonth.count} teklif`}
              delta={metrics.monthDeltaPct}
            />
            <StatTile
              label="Takipteki Teklifler"
              icon={<Hourglass className="w-4 h-4" />}
              value={formatCompactTL(metrics.pipeline.total)}
              valueTitle={formatFullTL(metrics.pipeline.total)}
              hint={metrics.pipeline.count > 0 ? `${metrics.pipeline.count} teklif yanıt bekliyor` : 'Yanıt bekleyen teklif yok'}
            />
            <StatTile
              label="Kazanma Oranı"
              icon={<Target className="w-4 h-4" />}
              value={metrics.results.winRate === null ? '—' : `%${metrics.results.winRate}`}
              hint={
                metrics.results.winRate === null
                  ? 'Henüz sonuçlanan teklif yok'
                  : `${metrics.results.won} kazanıldı · ${metrics.results.lost} kaybedildi`
              }
            />
            <StatTile
              label={`${now.getFullYear()} Kazanılan İş`}
              icon={<Trophy className="w-4 h-4" />}
              value={formatCompactTL(metrics.wonThisYear.total)}
              valueTitle={formatFullTL(metrics.wonThisYear.total)}
              hint={`${metrics.wonThisYear.count} teklif onaylandı`}
            />
          </div>

          {/* Two rows: what needs doing (left) beside where the portfolio stands (right) */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div className="lg:col-span-7">
              <SectionCard
                className="h-full"
                title="Aksiyon Gerekenler"
                subtitle="Geçerliliği dolan, yanıtı gecikmiş ve yarım kalan teklifler"
                action={
                  metrics.actions.length > 0 ? (
                    <span className="shrink-0 px-2 py-0.5 rounded-full bg-brand-100 text-brand-700 text-[10px] font-extrabold tabular-nums">
                      {metrics.actions.length}
                    </span>
                  ) : undefined
                }
              >
                <ActionList items={visibleActions} onOpen={onOpenProposal} />
                {hiddenActionCount > 0 && (
                  <button
                    type="button"
                    onClick={onOpenArchive}
                    className="w-full px-5 py-3 border-t border-stone-100 text-[11px] font-bold text-stone-500 hover:text-stone-900 hover:bg-stone-50 transition-colors text-left"
                  >
                    +{hiddenActionCount} teklif daha bekliyor — arşivi aç
                  </button>
                )}
              </SectionCard>
            </div>

            <div className="lg:col-span-5">
              <SectionCard
                className="h-full"
                title="Teklif Durumları"
                subtitle={`${metrics.total} teklif · ${formatFullTL(portfolioTotal)}`}
              >
                <StatusBreakdown slices={metrics.statusBreakdown} averageAmount={metrics.averageAmount} onSelect={onOpenArchive} />
              </SectionCard>
            </div>

            <div className="lg:col-span-7">
              <SectionCard
                className="h-full"
                title="Son Çalıştıkların"
                subtitle="Kaldığın yerden devam etmek için bir teklife tıkla"
                action={
                  <button
                    type="button"
                    onClick={onOpenArchive}
                    className="shrink-0 text-[10px] font-extrabold uppercase tracking-wider text-brand-600 hover:text-brand-700 transition-colors"
                  >
                    Tümü
                  </button>
                }
              >
                <RecentProposals items={metrics.recent} now={now} onOpen={onOpenProposal} />
              </SectionCard>
            </div>

            <div className="lg:col-span-5">
              <SectionCard
                className="h-full"
                title={`Son ${ACTIVITY_MONTHS} Ay`}
                subtitle="Aylık hazırlanan teklif sayısı"
              >
                <MonthlyActivity months={metrics.monthly} />
              </SectionCard>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
